/**
 * GLOBAL-FILTER-ENGINE-000001 — Universal Context Store (React).
 * Filter change → context → URL → modules. localStorage persistence.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_CONTEXT } from './defaults';
import {
  loadPersistedContext,
  loadPinnedFilterKeys,
  loadRecentFilterKeys,
  loadSavedViews,
  pushRecentFilterKey,
  removeSavedView as persistRemoveView,
  savePersistedContext,
  savePinnedFilterKeys,
  upsertSavedView,
} from './persistence';
import {
  applyContextToSearchParams,
  contextFromSearchParams,
  encodeContextQuery,
  patchContext,
} from './urlCodec';
import type { SavedFilterView, UniversalBusinessContext } from './types';
import { buildDeepLink } from './deepLink';

export type GlobalFilterApi = {
  context: UniversalBusinessContext;
  /** Partial update; syncs URL (replace) + localStorage. */
  setFilters: (patch: Partial<UniversalBusinessContext>) => void;
  setFilter: <K extends keyof UniversalBusinessContext>(
    key: K,
    value: UniversalBusinessContext[K],
  ) => void;
  resetFilters: () => void;
  /** Navigate preserving full context (+ optional patch). */
  navigateWithContext: (
    path: string,
    patch?: Partial<UniversalBusinessContext>,
    extra?: Record<string, string | undefined>,
  ) => void;
  buildLink: (
    path: string,
    patch?: Partial<UniversalBusinessContext>,
    extra?: Record<string, string | undefined>,
  ) => string;
  savedViews: SavedFilterView[];
  saveCurrentView: (name: string, opts?: { pinned?: boolean }) => void;
  applyView: (id: string) => void;
  deleteView: (id: string) => void;
  pinnedFilterKeys: string[];
  setPinnedFilterKeys: (keys: string[]) => void;
  recentFilterKeys: string[];
  contextQuery: string;
};

const GlobalFilterCtx = createContext<GlobalFilterApi | null>(null);

function mergeInit(locationSearch: string): UniversalBusinessContext {
  const stored = loadPersistedContext();
  const fromUrl = contextFromSearchParams(new URLSearchParams(locationSearch), stored);
  return fromUrl;
}

export function GlobalFilterProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [context, setContext] = useState<UniversalBusinessContext>(() =>
    mergeInit(location.search),
  );
  const [savedViews, setSavedViews] = useState<SavedFilterView[]>(() => loadSavedViews());
  const [pinnedFilterKeys, setPinnedKeysState] = useState(() => loadPinnedFilterKeys());
  const [recentFilterKeys, setRecentKeys] = useState(() => loadRecentFilterKeys());
  const skipNextUrlRead = useRef(false);
  const writing = useRef(false);

  // Persist + mirror URL when context changes from this app
  const commit = useCallback(
    (next: UniversalBusinessContext, pushHistory: boolean) => {
      setContext(next);
      savePersistedContext(next);
      const sp = applyContextToSearchParams(next, location.search);
      const qs = sp.toString();
      const nextSearch = qs ? `?${qs}` : '';
      if (nextSearch !== location.search) {
        skipNextUrlRead.current = true;
        writing.current = true;
        navigate(
          { pathname: location.pathname, search: nextSearch, hash: location.hash },
          { replace: !pushHistory },
        );
        writing.current = false;
      }
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  const setFilters = useCallback(
    (patch: Partial<UniversalBusinessContext>) => {
      for (const k of Object.keys(patch) as (keyof UniversalBusinessContext)[]) {
        setRecentKeys(pushRecentFilterKey(String(k)));
      }
      commit(patchContext(context, patch), false);
    },
    [commit, context],
  );

  const setFilter = useCallback(
    <K extends keyof UniversalBusinessContext>(key: K, value: UniversalBusinessContext[K]) => {
      setFilters({ [key]: value } as Partial<UniversalBusinessContext>);
    },
    [setFilters],
  );

  const resetFilters = useCallback(() => {
    const next = {
      ...DEFAULT_CONTEXT,
      lastWorkspacePath: context.lastWorkspacePath,
    };
    commit(next, false);
  }, [commit, context.lastWorkspacePath]);

  // Popstate / external search change → hydrate store
  useEffect(() => {
    if (skipNextUrlRead.current) {
      skipNextUrlRead.current = false;
      return;
    }
    if (writing.current) return;
    const merged = contextFromSearchParams(new URLSearchParams(location.search), context);
    const a = encodeContextQuery(merged);
    const b = encodeContextQuery(context);
    if (a !== b) {
      setContext(merged);
      savePersistedContext(merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-read on URL search
  }, [location.search]);

  // Track workspace path for "last workspace" restore
  useEffect(() => {
    if (!location.pathname || location.pathname === '/login') return;
    if (context.lastWorkspacePath === location.pathname) return;
    const next = { ...context, lastWorkspacePath: location.pathname };
    setContext(next);
    savePersistedContext(next);
    // don't rewrite URL solely for workspace path to avoid noise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const navigateWithContext = useCallback(
    (
      path: string,
      patch?: Partial<UniversalBusinessContext>,
      extra?: Record<string, string | undefined>,
    ) => {
      const href = buildDeepLink(path, context, patch, extra);
      if (patch) {
        const next = patchContext(context, patch);
        setContext(next);
        savePersistedContext(next);
      }
      navigate(href);
    },
    [context, navigate],
  );

  const buildLink = useCallback(
    (
      path: string,
      patch?: Partial<UniversalBusinessContext>,
      extra?: Record<string, string | undefined>,
    ) => buildDeepLink(path, context, patch, extra),
    [context],
  );

  const saveCurrentView = useCallback(
    (name: string, opts?: { pinned?: boolean }) => {
      const now = new Date().toISOString();
      const view: SavedFilterView = {
        id: `sv_${Date.now().toString(36)}`,
        name: name.trim() || 'Untitled view',
        kind: opts?.pinned ? 'pinned' : 'named',
        pinned: Boolean(opts?.pinned),
        createdAt: now,
        updatedAt: now,
        context: { ...context },
      };
      setSavedViews(upsertSavedView(view));
    },
    [context],
  );

  const applyView = useCallback(
    (id: string) => {
      const view = savedViews.find((v) => v.id === id);
      if (!view) return;
      commit({ ...view.context }, false);
    },
    [commit, savedViews],
  );

  const deleteView = useCallback((id: string) => {
    setSavedViews(persistRemoveView(id));
  }, []);

  const setPinnedFilterKeys = useCallback((keys: string[]) => {
    savePinnedFilterKeys(keys);
    setPinnedKeysState(keys);
  }, []);

  const value = useMemo<GlobalFilterApi>(
    () => ({
      context,
      setFilters,
      setFilter,
      resetFilters,
      navigateWithContext,
      buildLink,
      savedViews,
      saveCurrentView,
      applyView,
      deleteView,
      pinnedFilterKeys,
      setPinnedFilterKeys,
      recentFilterKeys,
      contextQuery: encodeContextQuery(context),
    }),
    [
      applyView,
      buildLink,
      context,
      deleteView,
      navigateWithContext,
      pinnedFilterKeys,
      recentFilterKeys,
      resetFilters,
      saveCurrentView,
      savedViews,
      setFilter,
      setFilters,
      setPinnedFilterKeys,
    ],
  );

  return <GlobalFilterCtx.Provider value={value}>{children}</GlobalFilterCtx.Provider>;
}

export function useGlobalFilter(): GlobalFilterApi {
  const v = useContext(GlobalFilterCtx);
  if (!v) throw new Error('useGlobalFilter must be used within GlobalFilterProvider');
  return v;
}

/** Optional consumer when outside provider (tests / rare). */
export function useGlobalFilterOptional(): GlobalFilterApi | null {
  return useContext(GlobalFilterCtx);
}
